/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { RadioProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import React, { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from '../../../../../registry/Components';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  label: {
    flex: 1,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
}));

function RadioProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string | number | boolean>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<RadioProp, string | number | boolean>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [options, setOptions] = useState<
    Record<string | number, string | number | boolean>
  >(typeof def.options === 'function' ? {} : def.options);

  useEffect(() => {
    if (typeof def.options === 'function') {
      const page = platform.getPage(appUid, pageId);
      const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
      def
        .options(itemWithDefaults, {
          appUid,
          pageId,
          itemId: activeItemId,
          mgr,
          platform,
        })
        .then((r) => {
          setOptions(r);
        });
    }
  }, [activeItemId, appUid, def, mgr, pageId, platform]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const _value = event.target.value as string;
    onChange(property, _value);
  };
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      disabled={isViewOnlyUser}
      data-testid={property}
      variant="standard"
      required={required}
      fullWidth
      component="fieldset"
      className={classes.root}
      size="small"
    >
      <FormLabel component="legend" className={classes.label}>
        {def.label}
      </FormLabel>
      <RadioGroup
        aria-label={def.label}
        name={def.label}
        value={value}
        onChange={handleChange}
        row
      >
        {Object.keys(options).map((key) => (
          <FormControlLabel
            key={key}
            value={key}
            control={<Radio />}
            label={
              <span className={classes.label}>{String(options[key])}</span>
            }
          />
        ))}
      </RadioGroup>
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(RadioProperty);
